import { TFunction } from 'i18next';

const BYTES_PER_MEGABYTE = 1024 * 1024;
const MAX_PARSE_ITERATIONS = 5;

export const toMegabytes = (bytes: number): number =>
  Math.floor(bytes / BYTES_PER_MEGABYTE);

export interface PresignedPostTarget {
  url: string;
  fields: unknown;
}

export function validateUploadFile(
  file: File,
  allowedMimeTypes: ReadonlySet<string>,
  maxFileSizeBytes: number,
  t: TFunction,
  onError: (msg: string) => void,
): boolean {
  if (file.size > maxFileSizeBytes) {
    onError(
      t('attachments.errors.fileTooLarge', {
        fileName: file.name,
        maxSize: toMegabytes(maxFileSizeBytes),
      }),
    );
    return false;
  }
  if (!allowedMimeTypes.has(file.type)) {
    onError(
      t('attachments.errors.fileTypeNotSupported', {
        fileName: file.name,
        fileType: file.type,
      }),
    );
    return false;
  }
  return true;
}

function parseFields(raw: unknown): Record<string, string> {
  let parsed: unknown = raw;
  let iterations = 0;

  while (typeof parsed === 'string') {
    if (iterations >= MAX_PARSE_ITERATIONS) {
      throw new Error('parseFields: exceeded max parse iterations');
    }
    try {
      parsed = JSON.parse(parsed);
    } catch {
      throw new Error('parseFields: failed to parse fields JSON');
    }
    iterations++;
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error('parseFields: expected a plain object');
  }

  return parsed as Record<string, string>;
}

export async function uploadToPresignedPost(
  target: PresignedPostTarget,
  file: File,
  signal?: AbortSignal,
): Promise<void> {
  const fields = parseFields(target.fields);
  const formData = new FormData();

  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);

  const response = await fetch(target.url, {
    method: 'POST',
    body: formData,
    signal,
  });
  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }
}
